import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Calendar,
  Trophy,
  Users,
  FileText,
  Download,
  Phone,
  Mail,
  AlertCircle,
  ShieldCheck,
  BookOpen,
  MapPin,
  ChevronRight,
} from "lucide-react";
import api, { formatDate, API } from "../lib/api";
import Seo from "../components/Seo";

const DAYS_SK = ["Nedeľa","Pondelok","Utorok","Streda","Štvrtok","Piatok","Sobota"];
const MONTHS_SK = ["január","február","marec","apríl","máj","jún","júl","august","september","október","november","december"];

function todayLong() {
  const d = new Date();
  return `${DAYS_SK[d.getDay()]} · ${d.getDate()}. ${MONTHS_SK[d.getMonth()]} ${d.getFullYear()}`;
}

export default function Home() {
  const [announcements, setAnnouncements] = useState([]);
  const [notices, setNotices] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [news, setNews] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [people, setPeople] = useState([]);
  const [contact, setContact] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/announcements").then((r) => setAnnouncements(r.data)),
      api.get("/notices").then((r) => setNotices(r.data)),
      api.get("/competitions").then((r) => setCompetitions(r.data)),
      api.get("/news").then((r) => setNews(r.data)),
      api.get("/documents").then((r) => setDocuments(r.data)),
      api.get("/people").then((r) => setPeople(r.data)),
      api.get("/contact-info").then((r) => setContact(r.data)),
      api.get("/settings").then((r) => setSettings(r.data)),
    ]).catch(() => {});
  }, []);

  const season = settings?.current_season || "2026/2027";
  const referees = people.filter((p) => p.type === "referee");
  const delegates = people.filter((p) => p.type === "delegate");
  const committee = people.filter((p) => p.type === "committee");

  return (
    <div data-testid="home-page">
      <Seo description="Oficiálny web Oblastného futbalového zväzu Levice — úradné správy komisií (ŠTK, DK, KR), súťaže VII. a VIII. ligy, rozpisy, rozhodcovia a delegáti, dokumenty a kontakty." />
      {/* ══════════ HERO ══════════ */}
      <section className="relative bg-green-950 text-white overflow-hidden noise">
        <div className="absolute inset-0 pitch-grid opacity-40 pointer-events-none" />
        {/* Right side accent */}
        <div className="absolute top-0 right-0 bottom-0 w-[45%] hidden lg:block">
          <div className="absolute inset-0 stripe-accent opacity-10" />
          <img
            src="https://images.unsplash.com/photo-1518604666860-9ed391f76460?auto=format&fit=crop&w=1200&q=80"
            alt=""
            className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-green-950 via-green-950/70 to-transparent" />
        </div>

        <div className="wrap relative py-16 md:py-28 lg:py-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <div className="eyebrow-light">Sezóna {season}</div>
              <span className="text-white/30">•</span>
              <span className="mono text-[11px] uppercase tracking-widest text-white/60">
                {todayLong()}
              </span>
            </div>

            <h1 className="display-xl text-white mb-8">
              Oblastný<br/>
              <span className="text-green-100">futbalový</span><br/>
              zväz Levice
            </h1>

            <p className="text-lg md:text-xl text-white/70 max-w-xl leading-relaxed mb-10">
              Oficiálne informácie, súťaže, rozpisy a oznamy pre oblastný futbal v levickom regióne.
              Od roku 1993 pre 40+ klubov, 600+ hráčov a desaťtisíce fanúšikov.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/uradne-spravy" className="btn btn-white btn-lg" data-testid="hero-btn-spravy">
                Aktuálna úradná správa <ArrowUpRight className="w-5 h-5" />
              </Link>
              <Link to="/rozpis" className="btn btn-outline-white btn-lg" data-testid="hero-btn-rozpis">
                Rozpis súťaže {season}
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative border-t border-white/10 bg-green-900/40 backdrop-blur-sm">
          <div className="wrap grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {[
              { value: "40+", label: "Klubov v okrese" },
              { value: "VII. — VIII.", label: "Oblastné ligy" },
              { value: "U9 — U19", label: "Mládežnícke kategórie" },
              { value: "1993", label: "Rok založenia" },
            ].map((s, i) => (
              <div key={i} className="px-5 md:px-8 py-6 first:pl-0 md:first:pl-8">
                <div className="font-heading font-extrabold text-2xl md:text-4xl text-white tracking-tight">
                  {s.value}
                </div>
                <div className="mono text-[11px] uppercase tracking-widest text-white/60 mt-1">
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ RED TICKER ══════════ */}
      {notices.filter((n) => n.type === "important").length > 0 && (
        <section className="bg-red text-white border-b-2 border-red">
          <div className="wrap py-3 flex items-center gap-4">
            <div className="flex items-center gap-2 flex-shrink-0 mono text-[11px] font-bold uppercase tracking-widest">
              <AlertCircle className="w-4 h-4" />
              Dôležité
            </div>
            <div className="ticker flex-1">
              <div className="ticker-track text-sm">
                {[...notices, ...notices].map((n, i) => (
                  <span key={i} className="inline-flex items-center gap-2">
                    <span className="mono text-white/70">{formatDate(n.date)}</span>
                    <span>—</span>
                    <span className="font-semibold">{n.title}:</span>
                    <span className="text-white/90">{n.excerpt}</span>
                    <span className="text-white/40 mx-3">///</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ══════════ DÔLEŽITÉ OZNAMY (magazine grid) ══════════ */}
      <section className="section-pad bg-paper">
        <div className="wrap">
          <div className="flex items-end justify-between mb-10 border-b-2 border-ink-900 pb-4">
            <div>
              <div className="eyebrow mb-3">Aktuálne oznamy</div>
              <h2 className="display-lg">Dôležité oznamy</h2>
            </div>
            <Link
              to="/uradne-spravy"
              className="hidden md:inline-flex items-center gap-1.5 mono text-xs font-bold uppercase tracking-widest text-ink-900 hover:text-green-700"
              data-testid="all-notices-link"
            >
              Archív <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-ink-100">
            {notices.map((n, i) => (
              <article
                key={n.id}
                className={`relative p-6 border-r border-b border-ink-100 bg-white hover:bg-cream transition-colors group`}
                data-testid={`notice-card-${n.id}`}
              >
                <div
                  className={`absolute top-0 left-0 right-0 h-1 ${
                    n.type === "important" ? "bg-red" : "bg-green-700"
                  }`}
                />
                <div className="flex items-center justify-between mb-4">
                  <span className="mono text-[11px] font-bold uppercase tracking-widest text-ink-500">
                    {formatDate(n.date)}
                  </span>
                  <span className={`mono text-[10px] font-bold uppercase tracking-widest ${
                    n.type === "important" ? "text-red" : "text-green-700"
                  }`}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="font-heading font-bold text-ink-900 text-lg leading-tight mb-3">
                  {n.title}
                </h3>
                <p className="text-sm text-ink-500 leading-relaxed line-clamp-3 mb-4">
                  {n.excerpt}
                </p>
                <Link
                  to={n.link?.startsWith("/") ? n.link : "/uradne-spravy"}
                  className="inline-flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-ink-900 group-hover:text-green-700"
                >
                  Zobraziť detail <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
                {n.attachment_url && (
                  <div className="mt-3">
                    <a href={n.attachment_url.startsWith("http") ? n.attachment_url : `${API}${n.attachment_url}`}
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-green-700 hover:text-green-800"
                       data-testid="notice-attachment">
                      <Download className="w-3.5 h-3.5" /> Príloha
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ NAJNOVŠIA ÚRADNÁ SPRÁVA (featured, full width) ══════════ */}
      {announcements[0] && (
        <section className="bg-white border-y-2 border-ink-900">
          <div className="wrap py-0">
            <Link
              to={`/uradne-spravy/${announcements[0].id}`}
              className="block bg-green-700 text-white p-8 md:p-12 relative overflow-hidden group hover:bg-green-800 transition-colors"
              data-testid={`announcement-row-${announcements[0].id}`}
            >
              <div className="absolute top-0 right-0 stripe-accent w-40 h-40 opacity-20" />
              <div className="flex items-center gap-3 mb-6 relative">
                <span className="tag tag-dark">Najnovšia úradná správa</span>
                <span className="mono text-[11px] font-bold uppercase tracking-widest text-white/80">
                  {announcements[0].category} · {announcements[0].number}
                </span>
                <span className="mono text-[11px] text-white/60">{formatDate(announcements[0].date)}</span>
              </div>
              <h3 className="display-md text-white mb-5 leading-tight max-w-4xl">
                {announcements[0].title}
              </h3>
              <p className="text-white/80 leading-relaxed max-w-3xl mb-8 line-clamp-3">
                {announcements[0].excerpt}
              </p>
              <span className="inline-flex items-center gap-2 mono text-xs font-bold uppercase tracking-widest text-white group-hover:gap-3 transition-all">
                Čítať celú správu <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* ══════════ RÝCHLY PRÍSTUP ══════════ */}
      <section className="section-pad bg-paper">
        <div className="wrap">
          <div className="flex items-end justify-between mb-10 border-b-2 border-ink-900 pb-4">
            <div>
              <div className="eyebrow mb-3">Pre kluby a verejnosť</div>
              <h2 className="display-lg">Rýchly prístup</h2>
            </div>
            <span className="hidden md:block mono text-xs text-ink-500 uppercase tracking-widest">
              Najčastejšie používané sekcie
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-ink-100">
            {[
              { icon: "📋", label: "Úradné správy", sub: "ŠTK · DK · KR", to: "/uradne-spravy" },
              { icon: "📅", label: `Rozpis ${season}`, sub: "Dokumenty", to: "/rozpis" },
              { icon: "🟢", label: "Obsadenie R", sub: "Aktuálne kolo", to: "/rozhodcovia" },
              { icon: "⚽", label: "Futbalnet", sub: "Výsledky live", href: contact?.futbalnet_url },
              { icon: "📖", label: "Pravidlá", sub: "Normy SFZ", to: "/dokumenty?cat=pravidla" },
              { icon: "☎", label: "Kontakt", sub: "Sekretariát", to: "/kontakt" },
            ].map((q, i) => {
              const content = (
                <div className="group relative p-6 md:p-7 border-r border-b border-ink-100 bg-white hover:bg-green-700 transition-colors h-full">
                  <div className="mono text-[10px] font-bold text-ink-400 group-hover:text-white/60 mb-3">
                    0{i + 1}/
                  </div>
                  <div className="text-2xl mb-4 grayscale group-hover:grayscale-0 transition-all">{q.icon}</div>
                  <div className="font-heading font-bold text-ink-900 text-base leading-tight group-hover:text-white">
                    {q.label}
                  </div>
                  <div className="mono text-[10px] uppercase tracking-widest text-ink-400 group-hover:text-white/70 mt-1">
                    {q.sub}
                  </div>
                  <ArrowUpRight className="absolute top-6 right-6 w-4 h-4 text-ink-300 group-hover:text-white transition-colors" />
                </div>
              );
              return q.href ? (
                <a key={i} href={q.href} target="_blank" rel="noopener noreferrer" data-testid={`quick-access-${i}`}>{content}</a>
              ) : (
                <Link key={i} to={q.to} data-testid={`quick-access-${i}`}>{content}</Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════ SÚŤAŽE (bold cards) ══════════ */}
      <section className="section-pad bg-ink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 pitch-grid opacity-30 pointer-events-none" />
        <div className="wrap relative">
          <div className="flex items-end justify-between mb-10 border-b border-white/20 pb-4">
            <div>
              <div className="eyebrow-light mb-3">Sezóna {season}</div>
              <h2 className="display-lg text-white">Oblastné súťaže</h2>
            </div>
            <Link to="/sutaze" className="mono text-xs font-bold uppercase tracking-widest text-white/80 hover:text-white inline-flex items-center gap-1.5">
              Všetky <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10">
            {competitions.map((c, i) => (
              <article
                key={c.id}
                className="bg-ink-900 p-7 md:p-9 hover:bg-ink-800 transition-colors group"
                data-testid={`competition-${c.id}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="mono text-[11px] font-bold uppercase tracking-widest text-green-100">
                      0{i + 1}/ {c.category === "seniors" ? "Seniori" : c.category === "youth" ? "Mládež" : "Pohár"}
                    </span>
                    <h3 className="font-heading font-extrabold text-white text-3xl md:text-4xl tracking-tight mt-2 leading-none">
                      {c.name}
                    </h3>
                  </div>
                  <div className="w-14 h-14 border-2 border-green-500 flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-6 h-6 text-green-100" />
                  </div>
                </div>
                <p className="text-white/60 text-sm leading-relaxed mb-6">{c.description}</p>
                <div className="flex flex-wrap gap-2">
                  {c.results_url && (
                    <a href={c.results_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/20 hover:border-green-500 hover:bg-green-500 text-white mono text-[11px] font-bold uppercase tracking-widest transition-all">
                      Výsledky <ArrowUpRight className="w-3 h-3" />
                    </a>
                  )}
                  {c.table_url && (
                    <a href={c.table_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/20 hover:border-green-500 hover:bg-green-500 text-white mono text-[11px] font-bold uppercase tracking-widest transition-all">
                      Tabuľka <ArrowUpRight className="w-3 h-3" />
                    </a>
                  )}
                  {c.schedule_url && (
                    <a href={c.schedule_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/20 hover:border-green-500 hover:bg-green-500 text-white mono text-[11px] font-bold uppercase tracking-widest transition-all">
                      Rozpis <ArrowUpRight className="w-3 h-3" />
                    </a>
                  )}
                  {c.documents_url && (
                    <Link to={c.documents_url}
                          className="inline-flex items-center gap-1.5 px-3 py-2 border border-white/20 hover:border-green-500 hover:bg-green-500 text-white mono text-[11px] font-bold uppercase tracking-widest transition-all">
                      Dokumenty
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ AKTUALITY (magazine editorial) ══════════ */}
      <section className="section-pad bg-paper">
        <div className="wrap">
          <div className="flex items-end justify-between mb-10 border-b-2 border-ink-900 pb-4">
            <div>
              <div className="eyebrow mb-3">Z činnosti zväzu</div>
              <h2 className="display-lg">Aktuality</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.slice(0, 6).map((n, i) => (
              <article key={n.id} className="group" data-testid={`news-${n.id}`}>
                <div className="aspect-[4/3] bg-ink-100 overflow-hidden mb-5 relative">
                  <div className="absolute inset-0 pitch-grid-light" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="font-heading font-extrabold text-8xl text-ink-200">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="tag tag-green">{n.category}</span>
                  </div>
                </div>
                <div className="mono text-[11px] uppercase tracking-widest text-ink-500 mb-2">
                  {formatDate(n.date)}
                </div>
                <h3 className="font-heading font-bold text-ink-900 text-xl leading-tight mb-2 group-hover:text-green-700 transition-colors">
                  {n.title}
                </h3>
                <p className="text-sm text-ink-500 leading-relaxed line-clamp-2">
                  {n.excerpt}
                </p>
                {n.link && (
                  <a
                    href={n.link.startsWith("/") ? undefined : n.link}
                    target={n.link.startsWith("/") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-ink-900 hover:text-green-700"
                  >
                    Čítať viac <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                )}
                {n.attachment_url && (
                  <div className="mt-3">
                    <a href={n.attachment_url.startsWith("http") ? n.attachment_url : `${API}${n.attachment_url}`}
                       target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1.5 mono text-[11px] font-bold uppercase tracking-widest text-green-700 hover:text-green-800"
                       data-testid="news-attachment">
                      <Download className="w-3.5 h-3.5" /> Príloha
                    </a>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ ROZHODCOVIA & DELEGÁTI ══════════ */}
      <section className="section-pad bg-white border-y border-ink-100">
        <div className="wrap">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-3">Komisia rozhodcov</div>
              <h2 className="display-lg mb-4">Rozhodcovia<br/>a delegáti</h2>
              <p className="text-ink-500 leading-relaxed max-w-xl mb-8">
                Aktuálne vedenie Komisie rozhodcov ObFZ Levice, delegáti zväzu
                a postup pre záujemcov o výkon funkcie rozhodcu v levickom okrese.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink-100 border border-ink-100">
                <div className="bg-white p-6">
                  <div className="mono text-[11px] font-bold uppercase tracking-widest text-green-700 mb-4">
                    Vedenie KR
                  </div>
                  <ul className="space-y-3 text-sm">
                    {committee.slice(0, 4).map((p) => (
                      <li key={p.id} className="flex justify-between gap-3 items-baseline border-b border-ink-100 pb-2 last:border-0 last:pb-0">
                        <span className="text-ink-900 font-semibold">{p.name}</span>
                        <span className="mono text-[10px] uppercase tracking-wider text-ink-400 text-right">{p.role}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-white p-6">
                  <div className="mono text-[11px] font-bold uppercase tracking-widest text-green-700 mb-4">
                    Delegáti zväzu
                  </div>
                  <ul className="space-y-3 text-sm">
                    {delegates.slice(0, 4).map((p) => (
                      <li key={p.id} className="flex justify-between gap-3 items-baseline border-b border-ink-100 pb-2 last:border-0 last:pb-0">
                        <span className="text-ink-900 font-semibold">{p.name}</span>
                        <span className="mono text-[10px] uppercase tracking-wider text-ink-400">{p.location}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Link to="/rozhodcovia" className="btn btn-ghost" data-testid="btn-referees">
                  Rozhodcovia <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/delegati" className="btn btn-ghost">
                  Delegáti <ArrowUpRight className="w-4 h-4" />
                </Link>
                {contact?.futbalnet_url && (
                  <a href={contact.futbalnet_url} target="_blank" rel="noopener noreferrer"
                     className="btn btn-primary" data-testid="btn-obsadenie">
                    Aktuálne obsadenie <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-green-700 text-white p-8 md:p-10 h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 stripe-accent w-32 h-32 opacity-20" />
                <div className="eyebrow-light mb-4">Pre záujemcov</div>
                <h3 className="font-heading font-extrabold text-white text-3xl leading-tight mb-4">
                  Staňte sa futbalovým rozhodcom
                </h3>
                <p className="text-white/80 leading-relaxed mb-8">
                  KR ObFZ Levice oznamuje záujemcom o výkon funkcie rozhodcu
                  z celého levického okresu, že v prípade záujmu sa môžu
                  kontaktovať telefonicky alebo e-mailom.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 border-t border-white/20 pt-3">
                    <Phone className="w-4 h-4 text-green-100 flex-shrink-0" />
                    <a href="tel:0903384831" className="font-mono text-white text-lg font-semibold hover:text-green-100">
                      0903 384 831
                    </a>
                  </li>
                  <li className="flex items-center gap-3 border-t border-white/20 pt-3">
                    <Phone className="w-4 h-4 text-green-100 flex-shrink-0" />
                    <a href="tel:0917774084" className="font-mono text-white text-lg font-semibold hover:text-green-100">
                      0917 774 084
                    </a>
                  </li>
                  <li className="flex items-center gap-3 border-t border-white/20 pt-3">
                    <Mail className="w-4 h-4 text-green-100 flex-shrink-0" />
                    <a href="mailto:kr@obfz-levice.sk" className="text-white text-lg font-semibold hover:text-green-100">
                      kr@obfz-levice.sk
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ KONTAKT ══════════ */}
      <section className="section-pad bg-white">
        <div className="wrap">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 border-2 border-ink-900">
            <div className="lg:col-span-5 p-8 md:p-12 bg-ink-900 text-white relative overflow-hidden">
              <div className="absolute bottom-0 right-0 stripe-accent w-40 h-40 opacity-15" />
              <div className="eyebrow-light mb-4">Sekretariát</div>
              <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-white leading-none tracking-tight mb-8">
                Kontakt
              </h2>

              <ul className="space-y-5">
                <li>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Sídlo</div>
                  <div className="text-white font-semibold">{contact?.organization || "Oblastný futbalový zväz Levice"}</div>
                  <div className="text-white/70 text-sm">{contact?.address || "Podjavorinskej 3, 934 01 Levice"}</div>
                </li>
                <li>
                  <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Sekretariát</div>
                  <a href={`mailto:${contact?.secretary_email || "sekretar@obfz-levice.sk"}`}
                     className="text-white font-semibold hover:text-green-100 font-mono">
                    {contact?.secretary_email || "sekretar@obfz-levice.sk"}
                  </a>
                </li>
                <li className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Telefón</div>
                    <a href={`tel:${(contact?.phone || "").replace(/\s+/g,"")}`} className="text-white font-semibold hover:text-green-100 font-mono">
                      {contact?.phone || "0903 384 831"}
                    </a>
                  </div>
                  <div>
                    <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Hodiny</div>
                    <div className="text-white text-sm">{contact?.office_hours || "Po – Pi: 8:00 – 15:00"}</div>
                  </div>
                </li>
              </ul>

              <Link to="/kontakt" className="mt-8 btn btn-white">
                Kompletné kontakty <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="lg:col-span-7 min-h-[400px] lg:min-h-[500px] bg-ink-100 relative">
              <iframe
                title="Mapa ObFZ Levice"
                className="absolute inset-0 w-full h-full border-0"
                loading="lazy"
                src="https://www.openstreetmap.org/export/embed.html?bbox=18.5742%2C48.2098%2C18.6142%2C48.2298&amp;layer=mapnik&amp;marker=48.2198%2C18.5942"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
